import { useState, KeyboardEvent, useEffect, useRef } from "react";

/**
 * Props for the useListboxKeyboardNavigation hook.
 * @template T - The type of items in the listbox
 */
interface UseListboxKeyboardNavigationProps<T> {
  /** Array of items to navigate through */
  items: T[];
  /** Callback invoked when an item is selected via Enter/Space */
  onSelect: (item: T) => void;
  /** The currently selected item, or null if none selected */
  selectedItem: T | null;
  /** Function to extract a unique ID from an item for aria-activedescendant */
  getItemId: (item: T) => string;
  /** Optional prefix for DOM element IDs (e.g., "conversation-" or "conversation-selector-") */
  idPrefix?: string;
}

/**
 * Return value from the useListboxKeyboardNavigation hook.
 */
interface UseListboxKeyboardNavigationReturn {
  /** Index of the currently focused item (-1 if none focused) */
  focusedIndex: number;
  /** Event handler for keyboard navigation, attach to the listbox container */
  handleKeyDown: (e: KeyboardEvent) => void;
  /** Returns the ID of the currently focused item for aria-activedescendant */
  getFocusedItemId: () => string | null;
}

/**
 * Custom hook for implementing WAI-ARIA listbox keyboard navigation pattern.
 *
 * Provides keyboard navigation for list-based UI components following accessibility
 * best practices including roving tabindex, arrow key navigation, and screen reader support.
 *
 * @template T - The type of items in the listbox
 *
 * @remarks
 * - Implements roving tabindex pattern (only focused item has tabIndex=0)
 * - Supports Arrow Up/Down, Home/End, Enter, and Space keys
 * - Focus ring only appears during keyboard navigation, not on page load
 * - Automatically syncs focus with selected item when selection changes
 * - Wraps navigation at list boundaries (ArrowDown at end goes to start)
 *
 * @see https://www.w3.org/WAI/ARIA/apg/patterns/listbox/
 */
export function useListboxKeyboardNavigation<T>({
  items,
  onSelect,
  selectedItem,
  getItemId,
  idPrefix = "",
}: UseListboxKeyboardNavigationProps<T>): UseListboxKeyboardNavigationReturn {
  // Initialize focused index to the selected item, or -1 if none selected
  const [focusedIndex, setFocusedIndex] = useState(() => {
    if (selectedItem) {
      const index = items.findIndex((item) => getItemId(item) === getItemId(selectedItem));
      return index >= 0 ? index : -1;
    }
    return -1;
  });

  /**
   * Track the previous selected item ID to detect actual selection changes.
   * This prevents unnecessary focus updates when the component re-renders
   * with the same selected item.
   */
  const prevSelectedItemRef = useRef<string | null>(null);

  /**
   * Track whether the user has used keyboard navigation yet.
   * This prevents the focus ring from appearing on page load - it only
   * shows after the user presses an arrow key or other navigation key.
   */
  const hasKeyboardNavigatedRef = useRef(false);

  /**
   * Sync the focused index with the selected item when selection changes.
   * This ensures that if the user clicks an item to select it, the keyboard
   * focus moves to that item for subsequent keyboard navigation.
   *
   * Also recalculates when the items array changes (e.g., when a conversation
   * is deleted) to keep the focusedIndex valid and in sync.
   */
  useEffect(() => {
    if (selectedItem) {
      const currentId = getItemId(selectedItem);
      // Only update if the selected item actually changed
      if (currentId !== prevSelectedItemRef.current) {
        prevSelectedItemRef.current = currentId;
        const index = items.findIndex((item) => getItemId(item) === currentId);
        if (index >= 0) {
          setFocusedIndex(index);
        }
      }
    }
  }, [selectedItem, getItemId, items]);

  /**
   * Keep focusedIndex in bounds when items array changes.
   * This handles cases where items are added/removed dynamically (e.g., when a
   * conversation is deleted while focused). Without this, focusedIndex could
   * point to a non-existent item, causing issues with keyboard navigation and
   * aria-activedescendant.
   */
  useEffect(() => {
    if (items.length === 0 && focusedIndex !== -1) {
      // If list becomes empty, reset to "nothing focused" state
      setFocusedIndex(-1);
    } else if (focusedIndex >= items.length) {
      // If focused index is out of bounds, move to the last valid item
      setFocusedIndex(items.length - 1);
    }
  }, [items, focusedIndex]);

  /**
   * Move browser focus to the focused element when focusedIndex changes.
   * This effect programmatically calls .focus() on the DOM element to move
   * the browser's focus, which is necessary for screen readers to announce
   * the focused item and for the focus ring to appear.
   *
   * Only applies focus if the user has used keyboard navigation at least once.
   * This prevents the focus ring from appearing on page load, which would be
   * visually distracting. The focus ring only appears after keyboard interaction.
   */
  useEffect(() => {
    if (hasKeyboardNavigatedRef.current && focusedIndex >= 0 && focusedIndex < items.length) {
      const itemId = getItemId(items[focusedIndex]);
      const fullId = idPrefix ? `${idPrefix}${itemId}` : itemId;
      const element = document.getElementById(fullId);

      if (element) {
        element.focus();
      }
    }
  }, [focusedIndex, items, getItemId, idPrefix]);

  /**
   * Keyboard event handler implementing WAI-ARIA listbox keyboard navigation.
   *
   * Supports the following keys:
   * - ArrowDown: Move focus to next item (wraps to first if at end)
   * - ArrowUp: Move focus to previous item (wraps to last if at start)
   * - Home: Move focus to first item
   * - End: Move focus to last item
   * - Enter/Space: Select the currently focused item
   *
   * All navigation keys set hasKeyboardNavigatedRef to true, which enables
   * programmatic focus and makes the focus ring visible.
   */
  const handleKeyDown = (e: KeyboardEvent) => {
    if (items.length === 0) {
      return;
    }

    switch (e.key) {
      case "ArrowDown":
        e.preventDefault();
        hasKeyboardNavigatedRef.current = true;
        setFocusedIndex((prev) => {
          // If nothing is focused, focus the first item
          if (prev === -1) {
            return 0;
          }
          // Otherwise, move down and wrap to beginning
          return (prev + 1) % items.length;
        });
        break;

      case "ArrowUp":
        e.preventDefault();
        hasKeyboardNavigatedRef.current = true;
        setFocusedIndex((prev) => {
          // If nothing is focused, focus the last item
          if (prev === -1) {
            return items.length - 1;
          }
          // Otherwise, move up and wrap to end
          return (prev - 1 + items.length) % items.length;
        });
        break;

      case "Home":
        e.preventDefault();
        hasKeyboardNavigatedRef.current = true;
        setFocusedIndex(0);
        break;

      case "End":
        e.preventDefault();
        hasKeyboardNavigatedRef.current = true;
        setFocusedIndex(items.length - 1);
        break;

      case "Enter":
      case " ":
        e.preventDefault();
        if (focusedIndex >= 0 && focusedIndex < items.length) {
          onSelect(items[focusedIndex]);
        }
        break;

      default:
        break;
    }
  };

  /**
   * Get the ID of the currently focused item for aria-activedescendant.
   *
   * This is used by the listbox container to set its aria-activedescendant
   * attribute, which tells screen readers which option currently has focus.
   *
   * @returns The ID string of the focused item, or null if no item is focused
   */
  const getFocusedItemId = (): string | null => {
    if (focusedIndex >= 0 && focusedIndex < items.length) {
      return getItemId(items[focusedIndex]);
    }
    return null;
  };

  return {
    focusedIndex,
    handleKeyDown,
    getFocusedItemId,
  };
}
